import { useEffect, useMemo, useRef, useState } from 'react';

type NumberMode = 'int' | 'float' | 'decimal';

interface BaseNumberInputProps {
  mode?: NumberMode;
  min?: number;
  max?: number;
  step?: number | string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  inputClassName?: string;
  "data-testid"?: string;
}

// Discriminated on `nullable` so existing call sites keep the strict
// number-only contract: an empty field commits null only when the caller
// opts in, otherwise it commits min (or 0) as before.
type NumberInputProps = BaseNumberInputProps &
  (
    | { nullable: true; value: number | null; onChange: (value: number | null) => void }
    | { nullable?: false; value: number; onChange: (value: number) => void }
  );

/**
 * NumberInput
 * 
 * UX: Avoid the classic controlled <input type="number"> issue where clearing the field forces "0"
 * and then typing produces "04" etc. This component keeps a local string while editing and
 * commits a numeric value on blur.
 * 
 * Stability: Parent state stays numeric; no changes to business logic or data shape required.
 */
export default function NumberInput(props: NumberInputProps) {
  const {
    value,
    mode = 'int',
    min,
    max,
    step,
    disabled,
    className,
    placeholder,
    "data-testid": dataTestId,
  } = props;
  // Nullable mode renders null as an empty field; non-nullable keeps the
  // legacy "null coerces to 0" display so existing call sites are unchanged.
  const toText = (v: number | null | undefined) =>
    props.nullable ? (v == null ? '' : String(v)) : String(v ?? 0);
  const [text, setText] = useState<string>(toText(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // When parent updates, we mirror the numeric value unless user is actively editing.
  const [focused, setFocused] = useState(false);

  const pattern = useMemo(() => {
    if (mode === 'float' || mode === 'decimal') return /^-?\d*(?:[.,]\d*)?$/;
    return /^-?\d*$/;
  }, [mode]);

  // Keep display in sync when not editing
  useEffect(() => {
    if (focused) return;
    setText(toText(value));
  }, [value, focused]);

  const clamp = (n: number) => {
    let out = n;
    if (Number.isFinite(min as number)) out = Math.max(out, min as number);
    if (Number.isFinite(max as number)) out = Math.min(out, max as number);
    return out;
  };

  const commitEmpty = () => {
    if (props.nullable) {
      props.onChange(null);
      setText('');
      return;
    }
    // When empty, set to min value (or 0) and update display
    const minVal = Number.isFinite(min as number) ? (min as number) : 0;
    props.onChange(minVal);
    setText(String(minVal));
  };

  const commit = () => {
    const raw = text.trim();
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
      commitEmpty();
      return;
    }

    const isDecimalMode = mode === 'float' || mode === 'decimal';
    const normalized = isDecimalMode ? raw.replace(',', '.') : raw;
    const parsed = isDecimalMode ? Number(normalized) : parseInt(normalized, 10);

    if (!Number.isFinite(parsed)) {
      commitEmpty();
      return;
    }

    const next = clamp(parsed);
    props.onChange(next);
    setText(String(next));
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode={mode === 'int' ? 'numeric' : 'decimal'}
      pattern={mode === 'int' ? '[0-9]*' : undefined}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onBeforeInput={(e) => {
        // For integer mode, block any character that is not a digit or
        // a leading minus sign. This prevents decimal separators ('.', ',')
        // from ever entering the field — even when the browser's numeric
        // keypad exposes them on mobile.
        if (mode !== 'int') return;
        const inputEvent = e as unknown as InputEvent;
        const data = inputEvent.data;
        if (data == null) return;
        if (!/^[0-9-]+$/.test(data)) {
          e.preventDefault();
        }
      }}
      onChange={(e) => {
        let next = e.target.value;

        // Strip leading zeros while preserving valid numbers
        // "05" -> "5", "005" -> "5", "-05" -> "-5", "00" -> "0", "0" -> "0"
        if (mode === 'int') {
          // For integers: remove leading zeros before any digit
          // Uses lookahead to keep at least one digit
          next = next.replace(/^(-?)0+(?=\d)/, '$1');
        } else {
          // For floats: remove leading zeros before digits
          next = next.replace(/^(-?)0+(?=\d)/, '$1');
          // Keep exactly one zero before decimal: "00.5" -> "0.5"
          next = next.replace(/^(-?)0+(?=[.,])/, '$10');
        }

        // Allow temporary empty state while typing
        if (next === '' || pattern.test(next)) {
          setText(next);

          // Preserve existing app behavior (immediate calculations) without forcing leading zeros.
          if (next === '' || next === '-' || next === '.' || next === '-.') return;

          const normalized = mode === 'float' ? next.replace(',', '.') : next;
          const parsed = mode === 'float' ? Number(normalized) : parseInt(normalized, 10);
          if (!Number.isFinite(parsed)) return;
          props.onChange(clamp(parsed));
        } else if (inputRef.current) {
          // Rejected input (e.g. pasted decimal in int mode): force the DOM
          // value back to the last valid state so the user never sees the
          // invalid character.
          inputRef.current.value = text;
        }
      }}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      aria-disabled={disabled}
      data-testid={dataTestId}
      // keep these as attributes for native keyboard hints even though type="text"
      min={min as any}
      max={max as any}
      step={mode === 'int' ? 1 : (step as any)}
    />
  );
}
